import { Image, StyleSheet, Text, View } from "@react-pdf/renderer"
import { DataNode } from "graphql/types"
import React from "react"
import { getImageURL } from "utils/getFileURL"
import { processInlineMarkdown } from "./markdown"

// Inline markdown is now handled via shared helper (bold, italic, links, code)

const styles = StyleSheet.create({
  imageContainer: {
    marginVertical: 15,
    pageBreakInside: "avoid",
  },
  singleImage: {
    width: "100%",
    maxHeight: 620,
    objectFit: "contain",
  },
  imageGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 10,
  },
  gridImage: {
    width: "100%",
    objectFit: "contain",
    marginBottom: 5,
  },
  imageCaption: {
    fontSize: 9,
    textAlign: "center",
    marginTop: 10,
    marginBottom: 5,
    color: "#000",
    lineHeight: 1.3,
  },
  imageCaptionBold: {
    fontWeight: "bold",
  },
  imageLabel: {
    fontSize: 9,
    color: "#666",
    textAlign: "center",
    marginTop: 3,
  },
})

interface ImageWithCaptionProps {
  node: DataNode
  globalImageCounter?: { current: number }
  chapterNumber?: string
  base64Images?: { [key: string]: string }
}

export const ImageWithCaption: React.FC<ImageWithCaptionProps> = ({
  node,
  globalImageCounter,
  chapterNumber,
  base64Images = {},
}) => {
  // ImageWithCaption component rendering
  // Generate image numbers if not provided
  const generateImageNumbers = () => {
    if (node.imageNumber && node.imageNumber.length > 0) {
      return node.imageNumber
    }

    if (globalImageCounter) {
      const numbers = []
      for (let i = 0; i < node.image.length; i++) {
        numbers.push(globalImageCounter.current++)
      }
      return numbers
    }

    return node.image.map((_, index) => index + 1)
  }

  const imageNumbers = generateImageNumbers()

  // Format caption using provided image number (no chapter prefix)
  const formatCaption = (imageNumber: number, caption?: string) => {
    const numberPart = `Figure ${imageNumber}`

    if (caption) {
      return (
        <Text style={styles.imageCaption}>
          <Text style={styles.imageCaptionBold}>{numberPart}:</Text> {caption}
        </Text>
      )
    }

    return (
      <Text style={styles.imageCaption}>
        <Text style={styles.imageCaptionBold}>{numberPart}</Text>
      </Text>
    )
  }

  // Helper to render image with fallback
  const renderImage = (imageId: string | undefined, style: any) => {
    if (!imageId) {
      return (
        <View
          style={[
            style,
            {
              backgroundColor: "#f5f5f5",
              justifyContent: "center",
              alignItems: "center",
              borderWidth: 1,
              borderColor: "#ddd",
            },
          ]}
        >
          <Text style={{ fontSize: 9, color: "#999" }}>No image available</Text>
        </View>
      )
    }

    // First try to use pre-loaded base64 image
    const base64Image = base64Images[imageId]
    if (base64Image) {
      let imageSource = base64Image

      // Fix mismatched MIME types - detect actual format from base64 data
      if (base64Image.startsWith("data:")) {
        const [header, base64Data] = base64Image.split(",")

        // Check if the MIME type matches the actual data
        // JPEG starts with /9j/ in base64
        // PNG starts with iVBORw0K in base64
        const isJpeg = base64Data.startsWith("/9j/")
        const isPng = base64Data.startsWith("iVBORw0K")

        if (isJpeg && !header.includes("image/jpeg")) {
          // Fix JPEG mislabeled as PNG
          imageSource = `data:image/jpeg;base64,${base64Data}`
          // Fixed JPEG mislabeled as PNG
        } else if (isPng && !header.includes("image/png")) {
          // Fix PNG mislabeled as JPEG
          imageSource = `data:image/png;base64,${base64Data}`
          // Fixed PNG mislabeled as JPEG
        }
      } else {
        // No data URL prefix, add one based on content
        const isJpeg = base64Image.startsWith("/9j/")
        const isPng = base64Image.startsWith("iVBORw0K")
        imageSource = isJpeg
          ? `data:image/jpeg;base64,${base64Image}`
          : `data:image/png;base64,${base64Image}`
      }

      // Using base64 image source

      return <Image src={imageSource} style={style} cache={false} debug={false} />
    }

    // Fallback to regular URL (may not work in PDF)
    const imageUrl = getImageURL(imageId)

    // Check if URL is valid
    if (!imageUrl || imageUrl === "/api/img/" || imageUrl === "http://localhost:8080/api/img/") {
      return (
        <View
          style={[
            style,
            {
              backgroundColor: "#f5f5f5",
              justifyContent: "center",
              alignItems: "center",
              borderWidth: 1,
              borderColor: "#ddd",
            },
          ]}
        >
          <Text style={{ fontSize: 9, color: "#999" }}>Image placeholder</Text>
        </View>
      )
    }

    // Try regular URL as last resort
    return <Image src={imageUrl} style={style} cache={false} debug={false} />
  }

  // Single image layout
  if (node.image.length === 1) {
    const imageObj = node.image[0]
    const imageId = imageObj
    const imageElement = renderImage(imageId, styles.singleImage)
    // Use individual image description if available, fallback to node.text
    const caption = node.imageCaption[0] || node.text

    return (
      <View style={styles.imageContainer}>
        {imageElement}
        {formatCaption(imageNumbers[0], caption)}
      </View>
    )
  }

  // Multiple images in grid layout
  return (
    <View style={styles.imageContainer} wrap={false}>
      <View style={styles.imageGrid}>
        {node.image.map((img, index) => {
          const imageId = img
          // Use individual image description if available, fallback to node.text
          const caption = node.imageCaption[index] || node.text

          return (
            <View key={imageId || `img-${index}`} style={{ width: "48%" }} wrap={false}>
              {renderImage(imageId, styles.gridImage)}
              {formatCaption(imageNumbers[index], caption)}
            </View>
          )
        })}
      </View>
    </View>
  )
}

// Table numbering component
interface TableWithCaptionProps {
  node: DataNode
  globalTableCounter?: { current: number }
  chapterNumber?: string
}

const tableStyles = StyleSheet.create({
  table: {
    width: "100%",
    marginVertical: 15,
    pageBreakInside: "avoid",
    wrap: false, // Prevent table from being split across pages
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableRowOdd: {
    backgroundColor: "#FFFFFF",
  },
  tableRowEven: {
    backgroundColor: "#F8F9FA",
  },
  tableHeader: {
    backgroundColor: "#E8F0F7",
    borderBottomWidth: 2,
    borderBottomColor: "#003366",
  },
  tableHeaderCell: {
    flex: 1,
    padding: 10,
    fontSize: 10,
    fontWeight: "bold",
    color: "#003366",
    borderRightWidth: 1,
    borderRightColor: "#D0D0D0",
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 9,
    borderRightWidth: 1,
    borderRightColor: "#E5E5E5",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E5E5",
    minHeight: 20,
    flexWrap: "wrap",
  },
  tableCaption: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    marginTop: 8,
    marginBottom: 10,
    color: "#000",
  },
  tableCaptionText: {
    fontWeight: "normal",
    fontStyle: "italic",
  },
})

export const TableWithCaption: React.FC<TableWithCaptionProps> = ({
  node,
  globalTableCounter,
  chapterNumber,
}) => {
  // Generate table number if not provided
  const tableNumber = node.tableNumber || (globalTableCounter ? globalTableCounter.current++ : 1)

  // Format caption using provided table number (no chapter prefix)
  const formatCaption = () => {
    const numberPart = `Table ${tableNumber}`

    if (node.tableCaption) {
      return (
        <Text style={tableStyles.tableCaption}>
          {numberPart}: <Text style={tableStyles.tableCaptionText}>{node.tableCaption}</Text>
        </Text>
      )
    }

    return <Text style={tableStyles.tableCaption}>{numberPart}</Text>
  }

  return (
    <View style={tableStyles.table} wrap={false}>
      {formatCaption()}
      <View style={[tableStyles.tableRow, tableStyles.tableHeader]}>
        {node.tableHeader.map((header, index) => (
          <Text
            key={index}
            style={[
              tableStyles.tableHeaderCell,
              { flex: header.colSpan || 1 },
              ...(index === node.tableHeader.length - 1 ? [{ borderRightWidth: 0 }] : []),
            ]}
          >
            {processInlineMarkdown(header.data)}
          </Text>
        ))}
      </View>
      {node.tableBody.map((row, rowIndex) => (
        <View
          key={rowIndex}
          style={[
            tableStyles.tableRow,
            rowIndex % 2 === 0 ? tableStyles.tableRowOdd : tableStyles.tableRowEven,
          ]}
        >
          {row.map((cell, cellIndex) => (
            <Text
              key={cellIndex}
              style={[
                tableStyles.tableCell,
                { flex: cell.colSpan || 1 },
                ...(cellIndex === row.length - 1 ? [{ borderRightWidth: 0 }] : []),
                ...(rowIndex === node.tableBody.length - 1 ? [{ borderBottomWidth: 0 }] : []),
              ]}
            >
              {processInlineMarkdown(cell.data)}
            </Text>
          ))}
        </View>
      ))}
    </View>
  )
}

// Export a manager class for tracking numbering across the document
export class DocumentNumberingManager {
  private imageCounter: number = 1
  private tableCounter: number = 1
  private figuresByChapter: Map<string, number> = new Map()
  private tablesByChapter: Map<string, number> = new Map()

  resetChapterNumbering(chapterId: string) {
    this.figuresByChapter.set(chapterId, 1)
    this.tablesByChapter.set(chapterId, 1)
  }

  getNextImageNumber(chapterId?: string): number {
    if (chapterId) {
      const current = this.figuresByChapter.get(chapterId) || 1
      this.figuresByChapter.set(chapterId, current + 1)
      return current
    }
    return this.imageCounter++
  }

  getNextTableNumber(chapterId?: string): number {
    if (chapterId) {
      const current = this.tablesByChapter.get(chapterId) || 1
      this.tablesByChapter.set(chapterId, current + 1)
      return current
    }
    return this.tableCounter++
  }

  reset() {
    this.imageCounter = 1
    this.tableCounter = 1
    this.figuresByChapter.clear()
    this.tablesByChapter.clear()
  }
}
